import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

/**
 * Diagrams are hand-authored SVG rather than a diagramming dependency: three static pictures do
 * not justify a renderer, and these inherit the app's own palette so they read as part of the
 * product rather than an embedded image.
 */
const NODE_H = 38;
const TONE = {
  browser: {
    fill: 'rgba(0,146,153,.12)',
    stroke: '#009299'
  },
  aws: {
    fill: 'rgba(0,180,136,.12)',
    stroke: '#00B488'
  },
  model: {
    fill: 'rgba(227,0,95,.10)',
    stroke: '#E3005F'
  },
  data: {
    fill: 'rgba(120,144,156,.14)',
    stroke: '#78909C'
  }
};
function Diagram(_ref) {
  let title = _ref.title,
    caption = _ref.caption,
    nodes = _ref.nodes,
    edges = _ref.edges,
    height = _ref.height;
  const byId = Object.fromEntries(nodes.map(n => [n.id, n]));
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      mb: '32px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 15,
      fontWeight: 600,
      mb: '4px'
    }
  }, title), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '12px',
      maxWidth: 680
    }
  }, caption), /*#__PURE__*/React.createElement(Box, {
    sx: {
      overflowX: 'auto',
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      p: '16px'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    component: "svg",
    viewBox: "0 0 860 ".concat(height),
    sx: {
      width: '100%',
      minWidth: 620,
      height: 'auto'
    }
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("marker", {
    id: "livy-arrow",
    viewBox: "0 0 10 10",
    refX: "9",
    refY: "5",
    markerWidth: "7",
    markerHeight: "7",
    orient: "auto"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0 L10 5 L0 10 z",
    fill: "currentColor",
    opacity: "0.5"
  }))), edges.map((_ref2, i) => {
    var _a$w, _b$w;
    let _ref3 = _slicedToArray(_ref2, 3),
      from = _ref3[0],
      to = _ref3[1],
      label = _ref3[2];
    const a = byId[from];
    const b = byId[to];
    if (!a || !b) return null;
    const aw = (_a$w = a.w) !== null && _a$w !== void 0 ? _a$w : 150;
    const sameRow = a.y === b.y;
    const x1 = sameRow ? a.x + aw : a.x + aw / 2;
    const y1 = sameRow ? a.y + NODE_H / 2 : a.y + NODE_H;
    const x2 = sameRow ? b.x : b.x + ((_b$w = b.w) !== null && _b$w !== void 0 ? _b$w : 150) / 2;
    const y2 = sameRow ? b.y + NODE_H / 2 : b.y;
    return /*#__PURE__*/React.createElement("g", {
      key: i,
      style: {
        color: 'currentColor'
      }
    }, /*#__PURE__*/React.createElement("line", {
      x1: x1,
      y1: y1,
      x2: x2,
      y2: y2,
      stroke: "currentColor",
      strokeOpacity: 0.35,
      strokeWidth: 1.4,
      markerEnd: "url(#livy-arrow)"
    }), label && /*#__PURE__*/React.createElement("text", {
      x: (x1 + x2) / 2,
      y: (y1 + y2) / 2 - 5,
      textAnchor: "middle",
      fontSize: "10.5",
      fill: "currentColor",
      fillOpacity: 0.62
    }, label));
  }), nodes.map(n => {
    var _n$tone, _n$w, _n$w2, _n$w3;
    const tone = TONE[(_n$tone = n.tone) !== null && _n$tone !== void 0 ? _n$tone : 'data'];
    return /*#__PURE__*/React.createElement("g", {
      key: n.id
    }, /*#__PURE__*/React.createElement("rect", {
      x: n.x,
      y: n.y,
      width: (_n$w = n.w) !== null && _n$w !== void 0 ? _n$w : 150,
      height: NODE_H,
      rx: 8,
      fill: tone.fill,
      stroke: tone.stroke,
      strokeWidth: 1.2
    }), /*#__PURE__*/React.createElement("text", {
      x: n.x + ((_n$w2 = n.w) !== null && _n$w2 !== void 0 ? _n$w2 : 150) / 2,
      y: n.y + (n.sub ? 16 : 23),
      textAnchor: "middle",
      fontSize: "11.5",
      fontWeight: 600,
      fill: "currentColor"
    }, n.label), n.sub && /*#__PURE__*/React.createElement("text", {
      x: n.x + ((_n$w3 = n.w) !== null && _n$w3 !== void 0 ? _n$w3 : 150) / 2,
      y: n.y + 29,
      textAnchor: "middle",
      fontSize: "9.5",
      fill: "currentColor",
      fillOpacity: 0.6
    }, n.sub));
  }))));
}
export function ArchitectureTab() {
  return /*#__PURE__*/React.createElement(Stack, {
    sx: {
      p: '4px 0 24px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 18,
      fontWeight: 600,
      mb: '4px'
    }
  }, "AI assistant architecture"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '22px',
      maxWidth: 720
    }
  }, "How a question becomes an answer, what the assistant is trusted with, and what it is not."), /*#__PURE__*/React.createElement(Diagram, {
    title: "A question, end to end",
    caption: "Nothing in the browser holds a key. The page signs with short-lived credentials from a Cognito identity pool, and the proxy re-verifies the user's own token before it will do anything, so a valid signature alone is not authority.",
    height: 210,
    nodes: [{
      id: 'browser',
      label: 'Browser',
      sub: 'Livy page or drawer',
      x: 10,
      y: 20,
      tone: 'browser'
    }, {
      id: 'pool',
      label: 'Identity pool',
      sub: 'SigV4 credentials',
      x: 220,
      y: 20,
      tone: 'aws'
    }, {
      id: 'url',
      label: 'Function URL',
      sub: 'AWS_IAM, streaming',
      x: 430,
      y: 20,
      tone: 'aws'
    }, {
      id: 'proxy',
      label: 'Chat proxy',
      sub: 'verifies JWT, mints grant',
      x: 640,
      y: 20,
      w: 200,
      tone: 'aws'
    }, {
      id: 'model',
      label: 'Anthropic',
      sub: 'MCP connector',
      x: 640,
      y: 120,
      w: 200,
      tone: 'model'
    }, {
      id: 'mcp',
      label: 'MCP service',
      sub: '.NET, tool layer',
      x: 400,
      y: 120,
      tone: 'aws'
    }, {
      id: 'svc',
      label: 'Livable services',
      sub: 'property, payment, allocation',
      x: 120,
      y: 120,
      w: 220,
      tone: 'data'
    }],
    edges: [['browser', 'pool', 'token'], ['pool', 'url', 'signed'], ['url', 'proxy'], ['proxy', 'model', 'tools + grant'], ['model', 'mcp'], ['mcp', 'svc']]
  }), /*#__PURE__*/React.createElement(Diagram, {
    title: "The connector grant",
    caption: "Anthropic never receives a Cognito token. The proxy mints an opaque handle scoped to one turn with a call budget and a TTL, and the authorizer validates it without spending it.",
    height: 150,
    nodes: [{
      id: 'proxy',
      label: 'Chat proxy',
      sub: 'mints lvg_ handle',
      x: 20,
      y: 20,
      w: 180,
      tone: 'aws'
    }, {
      id: 'store',
      label: 'Grant table',
      sub: 'budget + TTL',
      x: 250,
      y: 20,
      tone: 'data'
    }, {
      id: 'model',
      label: 'Anthropic',
      sub: 'holds the handle only',
      x: 480,
      y: 20,
      w: 180,
      tone: 'model'
    }, {
      id: 'authz',
      label: 'Grant authorizer',
      sub: 'validates, does not consume',
      x: 250,
      y: 95,
      w: 200,
      tone: 'aws'
    }],
    edges: [['proxy', 'store', 'write'], ['store', 'model'], ['store', 'authz']]
  }), /*#__PURE__*/React.createElement(Diagram, {
    title: "Writes are proposed, never performed",
    caption: "A model turn cannot change data. It names an intent and a target; the server reads the record, stores a snapshot and a hash, and a person confirms. Execution then runs as that person, not as the model.",
    height: 150,
    nodes: [{
      id: 'model',
      label: 'Model turn',
      sub: 'names intent + target',
      x: 20,
      y: 20,
      w: 180,
      tone: 'model'
    }, {
      id: 'server',
      label: 'Server',
      sub: 'reads record, hashes state',
      x: 250,
      y: 20,
      w: 190,
      tone: 'aws'
    }, {
      id: 'human',
      label: 'Person',
      sub: 'reads and confirms',
      x: 490,
      y: 20,
      w: 170,
      tone: 'browser'
    }, {
      id: 'exec',
      label: 'Execution',
      sub: 'runs as that person',
      x: 250,
      y: 95,
      w: 190,
      tone: 'aws'
    }],
    edges: [['model', 'server', 'propose'], ['server', 'human'], ['human', 'exec', 'confirm']]
  }));
}